import { Check, CloseOutlined } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import {
  type Company,
  type CompanySurvey,
  type CompanyTag,
  CompanyTagCategory,
  CompanyType,
  FundingRound,
  type Headcount,
} from "@prisma/client";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { CompanyHeadcountSelector } from "~/components/ui/CompanyHeadcountSelector";
import { CompanySizeSelector } from "~/components/ui/CompanySizeSelector";
import { type Group, SelectInput } from "~/components/ui/core/BetterSelectInput";
import { Button } from "~/components/ui/core/Button";
import { Icon } from "~/components/ui/core/Icon";
import { LegacyFormControl } from "~/components/ui/core/LegacyFormControl";
import { LegacyHeading } from "~/components/ui/core/LegacyHeading";
import { LegacyModal } from "~/components/ui/core/LegacyModal";
import { LegacyText } from "~/components/ui/core/LegacyText";
import { Ping } from "~/components/ui/core/Ping";
import { TextInput } from "~/components/ui/core/TextInput";
import { LegacyCompanyFundingRoundSelector } from "~/components/ui/LegacyCompanyFundingRoundSelector";
import { LegacyCompanyTagSelector } from "~/components/ui/LegacyCompanyTagSelector";
import { useApi } from "~/hooks/useApi";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { compact, get } from "~/lib/lodash";
import { formatFundingRounds } from "~/services/funding";

type CompanyWithSurvey = Company & {
  liveSurvey: CompanySurvey | null;
};

type Props = {
  title?: string;
  company?: CompanyWithSurvey;
  disabled?: boolean;
  input: Input;
  onChange: (value: Partial<Input>) => void;
};

export type NumberRange = {
  min: number | null;
  max: number | null;
};

type Input = {
  fundingRounds: FundingRound[];
  headcount: Headcount | null;
  industries: CompanyTag[];
  industry: CompanyTag | null;
  labels: CompanyTag[];
  size: NumberRange;
};

export const LegacyCompanyFilters: React.FC<Props> = ({ company, input, disabled = false, onChange }) => {
  const { t } = useI18n();
  const { CAN_USE_BENCHMARK_DATA_FILTERS_WITH_MULTIPLE_VALUES, CAN_ACCESS_FIGURES_AI_V2 } = useFeatureFlags();
  const { user, updateFlag } = useSession();
  const { apiFetch } = useApi();

  const Options = {
    employeesCount: {
      name: t("components.ui.legacy-company-filters.headcount"),
      icon: "group",
      description: t("components.ui.legacy-company-filters.headcount-description"),
    },
    lastFundingRound: {
      name: t("components.ui.legacy-company-filters.funding-round"),
      icon: "money-refund",
      description: t("components.ui.legacy-company-filters.funding-round-description"),
    },
    label: {
      name: t("components.ui.legacy-company-filters.label"),
      icon: "tag",
      description: t("components.ui.legacy-company-filters.label-description"),
    },
    industry: {
      name: t("components.ui.legacy-company-filters.industry"),
      icon: "factory",
      description: t("components.ui.legacy-company-filters.industry-description"),
    },
  } as const;

  type FilterName = keyof typeof Options;

  type UseCompanyFilterInput = {
    name: FilterName;
    input: {
      disabled: boolean;
      label: string;
      isDirty: boolean;
      html: JSX.Element;
      onClear: () => void;
    };
    prompt?: {
      enabled: boolean;
      title: string;
      html: JSX.Element;
    };
  };

  const useCompanyFilter = (props: UseCompanyFilterInput) => {
    const ref = useRef<HTMLDivElement>(null);
    const [showFilter, setShowFilter] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const showPrompt = showModal && props.prompt?.enabled;

    useEffect(() => {
      if (props.input.isDirty) {
        setShowFilter(true);
      }
    }, [props.input.isDirty]);

    const focusInput = () => {
      setTimeout(() => {
        ref.current?.querySelector("input")?.focus();
      }, 100);
    };

    const toggleInput = () => {
      if (showFilter) {
        setShowFilter(false);
        return;
      }

      if (props.prompt?.enabled) {
        setShowModal(true);
        return;
      }

      setShowFilter(true);

      focusInput();
    };

    const toggleModal = () => {
      if (showModal) {
        setShowModal(false);
        setShowFilter(true);
        focusInput();

        return;
      }

      setShowModal(true);
    };

    const html = (
      <div
        ref={ref}
        className={classNames({
          "flex flex-col": true,
          "mb-3": showFilter,
          "hidden": !showFilter,
        })}
      >
        <div className="flex items-center space-x-2">
          <LegacyHeading size="xs">{props.input.label}</LegacyHeading>
          {!props.input.disabled && (
            <Tooltip title={t("components.ui.legacy-company-filters.remove-this-filter")}>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => {
                  props.input.onClear();
                  setShowFilter(false);
                }}
              >
                <CloseOutlined className="text-xs" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {props.input.html}
      </div>
    );

    return {
      name: props.name,

      input: {
        isShown: showFilter,
        toggle: toggleInput,
        html,
      },

      ...(showPrompt && {
        prompt: {
          title: props.prompt?.title,
          toggle: toggleModal,
          html: props.prompt?.html,
        },
      }),
    };
  };

  const [surveyData, setSurveyData] = useState({
    employeesCount: company?.liveSurvey?.employeesCount ?? null,
    totalFunding: company?.liveSurvey?.totalFunding ?? null,
    lastFundingRound: company?.liveSurvey?.lastFundingRound ?? null,
  });

  const updateSurveyData = (input: Partial<typeof surveyData>) => {
    setSurveyData({ ...surveyData, ...input });
  };

  const employeesCountFilter = useCompanyFilter({
    name: "employeesCount",
    input: {
      label: t("components.ui.legacy-company-filters.number-of-employees"),
      isDirty: CAN_ACCESS_FIGURES_AI_V2 ? !!input.headcount : !!input.size.min || !!input.size.max,
      disabled,
      html: CAN_ACCESS_FIGURES_AI_V2 ? (
        <CompanyHeadcountSelector
          dense
          disabled={disabled}
          value={input.headcount as Headcount}
          onChange={async (event) => {
            onChange({ headcount: event.target.value ?? null });
          }}
        />
      ) : (
        <CompanySizeSelector
          disabled={disabled}
          value={input.size}
          onChange={(event) => {
            onChange({ size: event.target.value });
          }}
        />
      ),
      onClear: () => {
        if (CAN_ACCESS_FIGURES_AI_V2) {
          onChange({ headcount: null });
        } else {
          onChange({ size: { min: null, max: null } });
        }
      },
    },
    prompt: {
      enabled: company?.liveSurvey?.employeesCount === null && company.type === CompanyType.PARTICIPANT,
      title: t("components.ui.legacy-company-filters.headcount-prompt"),
      html: (
        <TextInput
          value={surveyData.employeesCount ?? ""}
          onChange={(event) => {
            updateSurveyData({
              employeesCount: event.target.value ? parseInt(event.target.value) : null,
            });
          }}
          type="number"
        />
      ),
    },
  });

  const lastFundingRoundFilter = useCompanyFilter({
    name: "lastFundingRound",
    input: {
      label: t("components.ui.legacy-company-filters.funding-round"),
      isDirty: input.fundingRounds.length > 0,
      disabled,
      html: CAN_ACCESS_FIGURES_AI_V2 ? (
        <LegacyCompanyFundingRoundSelector
          disabled={disabled}
          value={input.fundingRounds}
          onChange={(event) => {
            const fundingRounds = (event.target.value as string).split(",") as FundingRound[];
            onChange({ fundingRounds });
          }}
        />
      ) : (
        <LegacyCompanyFundingRoundSelector
          multiple
          disabled={disabled}
          value={input.fundingRounds}
          onChange={(event) => {
            const fundingRounds = (event.target.value as string[]).flatMap((group) =>
              group.split(",")
            ) as FundingRound[];
            onChange({ fundingRounds });
          }}
        />
      ),
      onClear: () => {
        onChange({ fundingRounds: [] });
      },
    },
    prompt: {
      enabled: company?.liveSurvey?.lastFundingRound === null && company.type === CompanyType.PARTICIPANT,
      title: t("components.ui.legacy-company-filters.funding-round-prompt"),
      html: (
        <SelectInput
          dense
          hideGroupLabels
          groups={[{ label: "", options: Object.values(FundingRound) }]}
          formatOption={(option) => {
            return formatFundingRounds(option.split(",") as FundingRound[]);
          }}
          formatLabel={(options) => {
            return (
              <TextInput
                data-input
                readOnly
                className="w-full cursor-pointer select-none rounded"
                placeholder="N/A"
                type="text"
                value={formatFundingRounds(options)}
              />
            );
          }}
          optionToId={(option) => option}
          placeholder="N/A"
          value={surveyData.lastFundingRound ? [surveyData.lastFundingRound] : []}
          onChange={(event) => {
            updateSurveyData({
              lastFundingRound: event.target.value[0] as FundingRound,
            });
          }}
        />
      ),
    },
  });

  const labelFilter = useCompanyFilter({
    name: "label",
    input: {
      label: t("components.ui.legacy-company-filters.label"),
      isDirty: input.labels.length > 0,
      disabled,
      html: (
        <LegacyCompanyTagSelector
          multiple={!CAN_ACCESS_FIGURES_AI_V2}
          categories={[CompanyTagCategory.LABEL, CompanyTagCategory.INTERNAL_BENCHMARK]}
          disabled={disabled}
          value={input.labels}
          onChange={(event) => {
            onChange({ labels: event.target.value });
          }}
        />
      ),
      onClear: () => {
        onChange({ labels: [] });
      },
    },
  });

  const industryFilter = useCompanyFilter({
    name: "industry",
    input: {
      label: t("components.ui.legacy-company-filters.industry"),
      isDirty: input.industries.length > 0 || input.industry !== null,
      disabled,
      html:
        CAN_USE_BENCHMARK_DATA_FILTERS_WITH_MULTIPLE_VALUES || !CAN_ACCESS_FIGURES_AI_V2 ? (
          <LegacyCompanyTagSelector
            multiple
            categories={[CompanyTagCategory.INDUSTRY]}
            disabled={disabled}
            value={input.industries}
            onChange={(event) => {
              onChange({ industries: event.target.value });
            }}
            hideGroupLabels={CAN_ACCESS_FIGURES_AI_V2 || CAN_USE_BENCHMARK_DATA_FILTERS_WITH_MULTIPLE_VALUES}
          />
        ) : (
          <LegacyCompanyTagSelector
            categories={[CompanyTagCategory.INDUSTRY]}
            disabled={disabled}
            value={compact([input.industry])}
            onChange={(event) => {
              onChange({ industry: event.target.value[0] ?? null });
            }}
            hideGroupLabels={CAN_ACCESS_FIGURES_AI_V2}
          />
        ),
      onClear: () => {
        onChange({ industries: [], industry: null });
      },
    },
  });

  const filters = [employeesCountFilter, lastFundingRoundFilter, labelFilter, industryFilter];

  const hasShownFilter = filters.some((filter) => filter.input.isShown);
  const hasAllShownFilters = filters.every((filter) => filter.input.isShown);
  const activeFilter = filters.find((filter) => !!filter.prompt);

  const groups: Group<FilterName>[] = [
    {
      label: t("components.ui.legacy-company-filters.select-filter"),
      options: filters.filter((filter) => !filter.input.isShown).map((filter) => filter.name),
    },
  ];

  const onFilterAdd = (name: FilterName) => {
    const filter = filters.find((filter) => filter.name === name);

    filter?.input.toggle();
  };

  const onPromptSubmit = async (name: FilterName) => {
    const value = get(surveyData, name);
    if (!value) {
      return;
    }

    await apiFetch("/api/fill-company-survey-value", {
      body: {
        field: name,
        value,
      },
      successMessage: t("components.ui.legacy-company-filters.company-survey-success-message"),
    });
  };

  return (
    <Stack>
      {/* the class is needed to scope the intercom tooltip to the results page */}
      <Stack spacing={2}>
        {!hasAllShownFilters && !disabled && (
          <SelectInput
            hideGroupLabels
            formatLabel={() => {
              return (
                <div
                  color="gray"
                  className="flex w-full content-center items-center justify-center rounded-md border border-gray-200 p-1 text-sm text-gray-600"
                >
                  {user?.flags.hasSeenCompaniesFilter === false ? (
                    <span className="mx-1 -mb-1 h-3">
                      <Ping size="xs" color="secondary" />
                    </span>
                  ) : (
                    <Icon className="text-gray-400" name="plus" />
                  )}

                  <span className="ml-1">
                    {hasShownFilter
                      ? t("components.ui.legacy-company-filters.add-a-filter")
                      : t("components.ui.legacy-company-filters.select-a-filter")}
                  </span>
                </div>
              );
            }}
            formatOption={(option) => {
              const { name, icon, description } = Options[option];

              return (
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-1.5">
                    <Icon className="text-gray-500" name={icon} size="sm" />
                    <span className="font-medium">{name}</span>
                  </div>
                  <span className="text-xs text-gray-500">{description}</span>
                </div>
              );
            }}
            groups={groups}
            optionToId={(option) => {
              return option;
            }}
            placeholder={t("components.ui.legacy-company-filters.filter-companies")}
            value={[]}
            width="xl"
            onChange={(event) => {
              const option = event.target.value[0];
              if (!option) {
                return;
              }

              onFilterAdd(option);
            }}
            onOpen={async () => {
              if (!user?.flags.hasSeenCompaniesFilter) {
                await updateFlag({ hasSeenCompaniesFilter: true });
              }
            }}
          />
        )}
      </Stack>

      <div
        className={classNames({
          "flex flex-col": true,
          "mt-3": hasShownFilter,
        })}
      >
        {filters.map((filter) => {
          return <div key={filter.name}>{filter.input.html}</div>;
        })}
      </div>

      <LegacyModal
        isOpen={!!activeFilter}
        title={t("components.ui.legacy-company-filters.help-us")}
        onClose={() => {
          activeFilter?.prompt?.toggle();
        }}
      >
        <div className="mt-5 flex flex-col">
          <LegacyHeading size="lg" className="mx-1.5 normal-case">
            {t("components.ui.legacy-company-filters.modal-heading")}
          </LegacyHeading>

          <div className="mx-1 mt-8 self-start">
            <LegacyFormControl label={<LegacyText className="normal-case">{activeFilter?.prompt?.title}</LegacyText>}>
              {activeFilter?.prompt?.html}
            </LegacyFormControl>
          </div>

          <div className="mt-12 flex items-center justify-between">
            <Button
              onClick={() => {
                activeFilter?.prompt?.toggle();
              }}
            >
              {t("components.ui.legacy-company-filters.skip-this-question")}
            </Button>

            <Button
              startIcon={<Check />}
              variant="contained"
              onClick={async () => {
                if (!activeFilter) {
                  return;
                }

                await onPromptSubmit(activeFilter.name);
                activeFilter.prompt?.toggle();
              }}
            >
              {t("components.ui.legacy-company-filters.see-market-data")}
            </Button>
          </div>
        </div>
      </LegacyModal>
    </Stack>
  );
};
